import { Box, Collapse, Typography } from '@mui/material';
import { LanguageOutlined, PersonOutline } from '@mui/icons-material';
import { ProviderMatchingProfileBookmark } from '@uplift/api';
import { VolunteerActivismIcon } from '@uplift/icons';
import { UserImage, CollapsableText } from '@uplift/ui-v2';


import {
  CollapseItem, SectionContainer, SubSectionContainer, SubSectionContainerWrapper
} from '../components';
import { useSelectedProviderFieldTransitionConfigs } from '../helpers';
import { useSelectedProviderContext } from '../selected-provider.context';
import {
  SelectedProviderSectionProps, SelectedProviderSubSectionEnum, SelectedProviderTabEnum
} from '../types';
import { ListItem } from '@uplift/types';

export type AboutMeSectionProps = SelectedProviderSectionProps & {
  selectedIdentityIds?: string[];
};

export const AboutMeSection: React.VFC<AboutMeSectionProps> = props => {
  const { selectedProvider } = useSelectedProviderContext();
  const { getTransitionConfig } = useSelectedProviderFieldTransitionConfigs(props.fieldTransitions);


  const bioTransitionConfig = getTransitionConfig(ProviderMatchingProfileBookmark.bio);
  const spokenLanguagesTransitionConfig = getTransitionConfig(ProviderMatchingProfileBookmark.languages);
  const genderTransitionConfig = getTransitionConfig(ProviderMatchingProfileBookmark.gender);
  const faithTransitionConfig = getTransitionConfig(ProviderMatchingProfileBookmark.faiths);
  const ethnicityTransitionConfig = getTransitionConfig(ProviderMatchingProfileBookmark.ethnicities);
  const therapeuticStylesTransitionConfig = getTransitionConfig(ProviderMatchingProfileBookmark.therapeuticStyles);

  const identifiesAs: ListItem[] = []
    .concat(ethnicityTransitionConfig.transitionIn ? selectedProvider.ethnicity : [])
    .concat(faithTransitionConfig.transitionIn ? selectedProvider.faith : [])
    .concat(genderTransitionConfig.transitionIn ? selectedProvider.genders : []);

  const sectionIn = [
    bioTransitionConfig.shown,
    spokenLanguagesTransitionConfig.shown,
    genderTransitionConfig.shown,
    faithTransitionConfig.shown,
    ethnicityTransitionConfig.shown,
    therapeuticStylesTransitionConfig.shown,
  ].some(s => s);

  return (
    <Collapse in={sectionIn} timeout={200}>
      <SectionContainer
        title="About Me"
        id={SelectedProviderTabEnum.about}
        forceMobile={props.forceMobile}
      >
        <SubSectionContainerWrapper
          id={SelectedProviderSubSectionEnum.bio}
          highlight={bioTransitionConfig.active}
          in={bioTransitionConfig.shown}
        >
          <Box
            px={{ xs: 2, md: 3 }}
            pt={{ xs: 2, md: 3 }}
            pb={2}
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="1rem"
          >
            <Box>
              <Box width="72px" height="72px">
                <UserImage imageUrl={selectedProvider.userProfile.avatarAsset?.url} maxWidth="100%" borderRadius="50%" />
              </Box>
            </Box>
            <Collapse in={bioTransitionConfig.transitionIn} timeout={500}>
              <CollapsableText previewMaxLength={200}>
                {selectedProvider.userProfile.shortBio}
              </CollapsableText>
            </Collapse>
          </Box>
        </SubSectionContainerWrapper>
        <SubSectionContainer
          title="Speaks:"
          icon={LanguageOutlined}
          contentInline
          hidden={!props.fieldTransitions && !selectedProvider.languages.length}
          id={SelectedProviderSubSectionEnum.spokenLanguages}
          forceMobile={props.forceMobile}
          highlight={spokenLanguagesTransitionConfig.active}
          in={spokenLanguagesTransitionConfig.shown}
        >
          <Typography component="span">
            {selectedProvider.languages.map((l) => l.name).join(', ')}
          </Typography>
        </SubSectionContainer>
        <SubSectionContainer
          title="Identifies as:"
          icon={PersonOutline}
          contentInline
          hidden={!props.fieldTransitions && !identifiesAs.length}
          id={SelectedProviderSubSectionEnum.identifiesAs}
          forceMobile={props.forceMobile}
          highlight={genderTransitionConfig.active || faithTransitionConfig.active || ethnicityTransitionConfig.active}
          in={genderTransitionConfig.shown}
        >
          <Typography component="span">
            {identifiesAs.map(({ id, name }, idx, { length }) => {
              let label: React.ReactNode = name.trim();

              if (props.selectedIdentityIds?.includes(id)) {
                label = <Typography component="b" fontWeight={500} key={id}>{name.trim()}</Typography>;
              }

              return <>{label}{idx !== length - 1 ? ', ' : ''}</>;
            })}
          </Typography>
        </SubSectionContainer>
        <SubSectionContainer
          title="Therapeutic Style:"
          icon={VolunteerActivismIcon}
          pb={3}
          hidden={!props.fieldTransitions && !selectedProvider.therapeuticStyles.length}
          id={SelectedProviderSubSectionEnum.therapeuticStyle}
          forceMobile={props.forceMobile}
          highlight={therapeuticStylesTransitionConfig.active}
          in={therapeuticStylesTransitionConfig.shown}
        >
          <Collapse in={therapeuticStylesTransitionConfig.transitionIn} timeout={500}>
            {selectedProvider.therapeuticStyles.map((t) => (
              <CollapseItem
                key={t.id}
                title={t.name}
                content={(
                  <Typography variant="body2">
                    {t.description}
                  </Typography>
                )}
              />
            ))}
          </Collapse>
        </SubSectionContainer>
      </SectionContainer>
    </Collapse>
  );
};
